import { useCallback, useEffect, useRef, useState } from "react";

/**
 * PWA 安裝檢測 Hook
 *
 * 功能：
 * 1. 檢測用戶是否已經將 APP 安裝到桌面
 * 2. 檢測是否以 PWA 模式運行（standalone）
 * 3. 監聽安裝事件
 * 4. 提供安裝提示功能
 *
 * @returns {Object} PWA 安裝狀態和相關方法
 */
export const usePwaInstall = () => {
  const [isStandalone, setIsStandalone] = useState(false);
  const [installSource, setInstallSource] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const deferredPromptRef = useRef(null);

  /**
   * 檢測是否以 PWA 模式運行（standalone）
   */
  const checkStandalone = useCallback(() => {
    if (typeof window === "undefined") return false;

    // 方法 1: 使用 matchMedia 檢測 display-mode
    const isStandaloneMode = window.matchMedia("(display-mode: standalone)").matches;

    // 方法 2: 檢查 navigator.standalone (iOS Safari)
    const isIOSStandalone =
      typeof window.navigator !== "undefined" && window.navigator.standalone === true;

    // 方法 3: 檢查 URL 參數或 localStorage（你們現有的方法）
    const isAppParam = new URLSearchParams(window.location.search).get("app");
    // const isAppLocalStorage = localStorage.getItem(localStorageKey.IS_APP);

    return (!!isIOSStandalone || !!isStandaloneMode) && !isAppParam;
  }, []);

  /**
   * 獲取安裝來源（移動端或桌面端）
   */
  const getInstallSource = useCallback(() => {
    if (typeof window === "undefined") return null;

    const userAgent = window.navigator.userAgent || "";
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );

    // 檢測平台
    let platform = "desktop";
    if (/Android/i.test(userAgent)) {
      platform = "android";
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      platform = "ios";
    }

    return {
      device: isMobile ? "mobile" : "desktop",
      platform,
      userAgent,
    };
  }, []);

  /**
   * 觸發 PWA 安裝提示
   */
  const promptInstall = useCallback(async () => {
    if (!deferredPromptRef.current) {
      console.log("PWA install prompt is not available");
      return false;
    }

    try {
      // 顯示安裝提示
      deferredPromptRef.current.prompt();

      // 等待用戶回應
      const { outcome } = await deferredPromptRef.current.userChoice;

      // 清除保存的 prompt
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      setCanInstall(false);

      if (outcome === "accepted") {
        console.log("User accepted the install prompt");
        return true;
      } else {
        console.log("User dismissed the install prompt");
        return false;
      }
    } catch (error) {
      console.error("Error showing install prompt:", error);
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      setCanInstall(false);
      return false;
    }
  }, []);

  // 監聽 beforeinstallprompt 事件
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleBeforeInstallPrompt = (e) => {
      // 阻止默認的安裝提示
      e.preventDefault();
      // 保存事件對象
      deferredPromptRef.current = e;
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  // 監聽 appinstalled 事件
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleAppInstalled = () => {
      console.log("PWA was installed");
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      setCanInstall(false);
      // 重新檢測是否為 standalone 模式
      const standalone = checkStandalone();
      setIsStandalone(standalone);
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [checkStandalone]);

  // 初始化檢測
  useEffect(() => {
    if (typeof window === "undefined") return;

    // 初始檢測
    const standalone = checkStandalone();
    const source = getInstallSource();

    setIsStandalone(standalone);
    setInstallSource(source);

    // 如果已經安裝，保存到 localStorage
    // if (installed && !localStorage.getItem("IS_APP")) {
    //   localStorage.setItem("IS_APP", "true");
    // }
  }, [checkStandalone, getInstallSource]);

  return {
    // 狀態
    isStandalone, // 是否以 standalone 模式運行
    installSource, // 安裝來源信息 { device, platform, userAgent }
    canInstall, // 是否可以安裝
    deferredPrompt, // 安裝提示事件對象

    // 方法
    checkStandalone, // 重新檢測是否為 standalone 模式
    promptInstall, // 觸發安裝提示
  };
};
